/* eslint-disable indent */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { rgba } from 'polished';
import ReactModal from 'react-modal';
import ModalHeader from './modal-header';
import ModalBody from './modal-body';
import ModalFooter from './modal-footer';
import { Title } from '../type';

export const ModalTitle = styled(Title)`
	margin-bottom: 0;
`;

function ReactModalAdapter({ className, transition = true, parentSelector, ...props }) {
	if( !className ) {
		className = 'px-react-modal';
	}

	const contentClassName = `${className}__content`;
	const overlayClassName = `${className}__overlay`;

	// Wrap parentSelector to guard against null returns (Fixes 3X-1MJ)
	const safeParentSelector = parentSelector
		? () => parentSelector() || document.body
		: undefined;

	return (
		<ReactModal
			portalClassName={className}
			className={contentClassName}
			overlayClassName={overlayClassName}
			bodyOpenClassName="px-modal-open"
			htmlOpenClassName="px-modal-open"
			closeTimeoutMS={250}
			ariaHideApp={false}
			transition={transition}
			parentSelector={safeParentSelector}
			{...props}
		/>
	);
}
ReactModalAdapter.propTypes = {
	className: PropTypes.string,
	transition: PropTypes.bool,
	parentSelector: PropTypes.func
};
ReactModalAdapter.displayName = 'ReactModalAdapter';

const Modal = styled(ReactModalAdapter)`
	&__overlay {
		position: fixed;
		display: flex;
		align-items: center;
		justify-content: center;
		top: 0;
		left: 0;
		width: 100vw;
		height: 100vh;
		background-color: ${({ theme }) => rgba(theme['black'], 0.75)};
		transition: ${({ transition }) => transition ? 'opacity 250ms ease' : 'none'};
		opacity: 0;
		z-index: 100;

		&.ReactModal__Overlay--after-open {
			opacity: 1;
		}

		&.ReactModal__Overlay--before-close {
			opacity: 0;
		}
	}

	&__content {
		width: ${(props) => {
			if( typeof props.size === 'number' ) {
				return `${props.size}px`;
			}

			switch( props.size ) {
				case 'sm':
					return '300px';

				case 'lg':
					return '800px';

				case 'xl':
					return '1200px';

				default:
					return '600px';
			}
		}};
		max-width: calc(100% - 3.5rem);
		max-height: calc(100% - 3.5rem);
		background: ${({ theme }) => theme['midnight-lighten-5']};
		overflow: auto;
		-webkit-overflow-scrolling: touch;
		border-radius: 0.25rem;
		outline: none;
		transition: ${({ transition }) => transition ? '250ms cubic-bezier(0.68, -0.55, 0.265, 1.55)' : 'none'};
		transform: ${({ transition }) => transition ? 'scale(0.95)' : 'none'};
		color: ${({ theme }) => theme['white']};

		&.ReactModal__Content--after-open {
			transform: ${({ transition }) => transition ? 'scale(1)' : 'none'};
		}

		&.ReactModal__Content--before-close {
			transform: ${({ transition }) => transition ? 'scale(0.95)' : 'none'};
		}

		& > * {
			&:not(:last-child) {
				margin-bottom: 1rem;
			}
		}
	}
`;

Modal.propTypes = {
	size: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]),
	transition: PropTypes.bool
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Title = ModalTitle;

export default Modal;
